import { useEffect, useState } from "react";
import axios from "axios";
import { Play, Video, GraduationCap, CalendarDays, Clock, Loader2, Newspaper, ArrowRight, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  meeting: { label: "Monthly meeting", Icon: Video, badge: "bg-[#0F2A47] text-white" },
  training: { label: "Training clip", Icon: GraduationCap, badge: "bg-[#1F8F8B] text-white" },
};

const FieldUpdates = ({ getAuthHeader }) => {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/library/field-updates?limit=10`, { headers: getAuthHeader() })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <section className="mb-10" data-testid="field-updates-section">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#1F8F8B] font-bold">
        <Newspaper className="w-3.5 h-3.5" /> Weekly Field Updates
      </div>
      <h3 className="mt-1 font-semibold text-xl text-[#0F2A47]">Watch it or read it</h3>
      <p className="mt-1 text-sm text-[#59564C]">
        Every weekly update ships with a video and the same content written out. Expand any week to read the brief.
      </p>
      <div className="mt-4 rounded-2xl bg-white border border-[#EDE9DC] divide-y divide-[#EDE9DC] overflow-hidden">
        {rows.map((f) => {
          const open = openId === f.id;
          return (
            <div key={f.id} data-testid={`field-update-row-${f.id}`}>
              <button
                onClick={() => setOpenId(open ? null : f.id)}
                data-testid={`field-update-expand-${f.id}`}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#FDFBF7] transition-colors"
              >
                {f.youtube_id ? (
                  <img src={`https://img.youtube.com/vi/${f.youtube_id}/default.jpg`} alt="" className="shrink-0 w-16 h-10 rounded-lg object-cover border border-[#EDE9DC]" loading="lazy" />
                ) : (
                  <span className="shrink-0 w-16 h-10 rounded-lg bg-[#0F2A47] flex items-center justify-center text-white/70"><Newspaper className="w-4 h-4" /></span>
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-[#0F2A47] truncate">{f.subject}</span>
                  <span className="block text-[11px] text-[#7A7668]">
                    {(f.created_at || "").slice(0, 10)}{f.brief ? " · written brief" : ""}
                  </span>
                </span>
                <ChevronDown className={`w-4 h-4 text-[#7A7668] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="px-4 pb-5 pt-1 sm:px-6" data-testid={`field-update-body-${f.id}`}>
                  {f.brief ? (
                    <div className="grc-rte-view rounded-xl bg-[#FDFBF7] border border-[#EDE9DC] p-4 text-[14px] text-[#0F2A47] leading-relaxed" dangerouslySetInnerHTML={{ __html: f.brief }} />
                  ) : (
                    <p className="text-[14px] text-[#0F2A47] leading-relaxed whitespace-pre-wrap">{f.message}</p>
                  )}
                  {f.action_item && (
                    <div className="mt-3 rounded-xl bg-[#A6D24A22] border border-[#A6D24A] px-4 py-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#0F2A47] font-bold">Action item</div>
                      <div className="mt-0.5 text-sm font-semibold text-[#0F2A47]">{f.action_item}</div>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a href={`/fieldupdate/${f.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-[#0F2A47] text-white px-3.5 py-1.5 rounded-full hover:bg-[#173a5f] transition-colors">
                      Open full update <ArrowRight className="w-3 h-3" />
                    </a>
                    {f.video_url && (
                      <a href={f.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-white border border-[#EDE9DC] text-[#0F2A47] px-3.5 py-1.5 rounded-full hover:border-[#1F8F8B] transition-colors">
                        <Play className="w-3 h-3" fill="currentColor" /> Watch the video
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const ReplaysGrid = ({ getAuthHeader }) => {
  const [replays, setReplays] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/library/replays`, { headers: getAuthHeader() })
      .then((r) => setReplays(r.data || []))
      .catch(() => setReplays([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (replays === null) {
    return (
      <div className="py-20 flex items-center justify-center text-[#7A7668]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading replays…
      </div>
    );
  }
  return (
    <div>
      <FieldUpdates getAuthHeader={getAuthHeader} />
      {replays.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#D9D3BF] bg-white/60 py-16 text-center" data-testid="replays-empty">
          <Video className="w-8 h-8 text-[#7A7668] mx-auto mb-2" />
          <p className="text-[#0F2A47] font-medium">No replays yet.</p>
          <p className="text-sm text-[#7A7668] mt-1">Recorded partner meetings and training clips will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="replays-grid">
          {replays.map((r) => {
            const meta = KIND_META[r.kind] || KIND_META.meeting;
            return (
              <a
                key={r.id}
                href={r.video_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`replay-card-${r.id}`}
                className="group rounded-2xl bg-white border border-[#EDE9DC] shadow-sm overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="relative aspect-video bg-[#0F2A47] overflow-hidden">
                  {r.youtube_id ? (
                    <img
                      src={`https://img.youtube.com/vi/${r.youtube_id}/hqdefault.jpg`}
                      alt={r.title}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #08192F 0%, #17385e 60%, #1F8F8B 130%)" }} />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#A6D24A] text-[#08192F] shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
                    </span>
                  </div>
                  <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${meta.badge}`}>
                    <meta.Icon className="w-3 h-3" /> {meta.label}
                  </span>
                </div>
                <div className="p-4">
                  <div className="font-semibold text-[#0F2A47] leading-snug">{r.title}</div>
                  {r.description && <p className="mt-1 text-[13px] text-[#59564C] leading-snug line-clamp-2">{r.description}</p>}
                  <div className="mt-2.5 flex items-center gap-3 text-[11px] text-[#7A7668]">
                    {r.recorded_on && (
                      <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {r.recorded_on}</span>
                    )}
                    {r.duration_label && (
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.duration_label}</span>
                    )}
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};
